import { MouseEvent, useRef, useState, useMemo, useEffect, useCallback } from "react";
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    BackgroundVariant,
    NodeTypes,
    EdgeTypes,
    useNodesState,
    useEdgesState,
    Node,
    Edge
} from 'reactflow';
import 'reactflow/dist/style.css';
import ELK, { ElkNode } from "elkjs";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationPolicyConfigDecision
} from "@idw-types/data/rp-service-configuration";
import { WorkflowVisualizationPolicyConfigEdge, WorkflowVisualizationPolicyConfigNode } from "@idw-types/common/workflow-visualization";
import { getPolicyConfigEdges, getPolicyConfigNodes } from "@utils/workflow-visualization/policy-configuration-util";
import { WorkflowVisualizationServiceEdge } from "./workflow-visualization-service-edge/workflow-visualization-service-edge";
import { WorkflowVisualizationServiceNode } from "./workflow-visualization-service-node/workflow-visualization-service-node";
import { WorkflowVisualizationServiceContextMenu } from "@containers/common/workflow-visualization-container/workflow-visualization-policy-configs-container/workflow-visualization-service-context-menu/workflow-visualization-service-context-menu";
import { WorkflowVisualizationPolicyDecisionContextMenu } from "@containers/common/workflow-visualization-container/workflow-visualization-policy-configs-container/workflow-visualization-policy-context-menu/workflow-visualization-policy-context-menu";
import styles from "./workflow-visualization-policy-configs-container.less";

type WorkflowVisualizationPolicyConfigsContainerProps = {
    rpServices: Array<FormattedRpServiceConfiguration>;
    primaryRpServiceId: string;
    hasUnsubmittedChangeRequest: boolean;
    onClickOpenPolicyDecisionDetails: (
        policyDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision>,
        rpService: FormattedRpServiceConfiguration,
        isDefaultAction: boolean
    ) => void;
    onClickOpenServiceDetails: (service: FormattedRpServiceConfiguration) => void;
    onClickArchiveService: (id: string) => void;
    onClickSetAsPrimaryService: (id: string) => void;
};

export const WorkflowVisualizationPolicyConfigsContainer = (
    props: WorkflowVisualizationPolicyConfigsContainerProps
) => {
    const {
        rpServices,
        primaryRpServiceId,
        hasUnsubmittedChangeRequest,
        onClickOpenServiceDetails,
        onClickArchiveService,
        onClickSetAsPrimaryService,
        onClickOpenPolicyDecisionDetails
    } = props;
    const menuRef = useRef(null);

    const nodeTypes: NodeTypes = useMemo(() => ({
        workflowVisualizationServiceNode: WorkflowVisualizationServiceNode
    }), []);

    const edgeTypes: EdgeTypes = useMemo(() => ({
        workflowVisualizationServiceEdge: WorkflowVisualizationServiceEdge
    }), []);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [nodeMenu, setNodeMenu] = useState<{
        id: string;
        top: number;
        left: number;
        right: number;
        bottom: number;
    }>(null);
    const [edgeMenu, setEdgeMenu] = useState<{
        id: string;
        top: number;
        left: number;
        right: number;
        bottom: number;
    }>(null);
    const ref = useRef<HTMLDivElement>(null);

    const performLayout = async (
        nodes: Array<WorkflowVisualizationPolicyConfigNode>,
        edges: Array<WorkflowVisualizationPolicyConfigEdge>
    ): Promise<Array<ElkNode>> => {
        const elk = new ELK();

        const graph: ElkNode = {
            id: "root",
            children: nodes.map((element) => ({
                ...element,
                width: 350,
                height: 200,
            })),
            edges: edges as any,
        };

        const processedGraph = await elk.layout(graph, {
            layoutOptions: {
                "elk.algorithm": "layered",
                "elk.direction": "DOWN",
                "nodePlacement.strategy": "BRANDES_KOEPF",
                "nodePlacement.favorStraightEdges": "false",
                'elk.spacing.nodeNode': '50', // Espacement horizontal entre les nœuds
                'elk.spacing.edgeNode': '30', // Espacement entre les arêtes et les nœuds
                'elk.layered.spacing.nodeNodeBetweenLayers': '100', // Espacement
            }
        });
        const layoutedElements = processedGraph.children.map((node) => ({
            ...node,
            position: {
                x: node.x+500,
                y: node.y,
            },
        }));

        return layoutedElements;
    };

    const getNodesForFlow = (
        initialNodes: Array<WorkflowVisualizationPolicyConfigNode>,
        elkNodes: Array<ElkNode>
    ) => {
        const finalNodes: Array<any> = [];
        elkNodes.forEach(eNode => {
            const initalNode = initialNodes.find(iNode => iNode.id === eNode.id);
            if (initalNode) {
                finalNodes.push({
                    ...initalNode,
                    position: { x: eNode.x, y: eNode.y }
                });
            }
        });
        return finalNodes;
    };

    const generatePolicyConfigNodesAndEdges = async () => {
        const nodes = getPolicyConfigNodes(rpServices, primaryRpServiceId);
        const edges = getPolicyConfigEdges(rpServices, nodes);
        const layoutedData = await performLayout(nodes, edges);
        const layoutedNodes = getNodesForFlow(nodes, layoutedData);
        setNodes(layoutedNodes);
        setEdges(edges);
    };

    useEffect(() => {
        generatePolicyConfigNodesAndEdges();
    }, [rpServices, primaryRpServiceId]);

    const onNodeContextMenu = useCallback((event: MouseEvent, node: Node) => {
        // Prevent native context menu from showing
        event.preventDefault();

        if (!node.id.includes("-")) {
            // Calculate position of the context menu. We want to make sure it
            // doesn't get positioned off-screen.
            const pane = ref.current.getBoundingClientRect();
            setNodeMenu({
                id: node.id,
                top: event.clientY < pane.height - 200 && (event.clientY - pane.y),
                left: event.clientX < pane.width - 200 && (event.clientX - pane.x),
                right: event.clientX >= pane.width - 200 && (pane.width - event.clientX + pane.x),
                bottom: event.clientY >= pane.height - 200 && (pane.height - event.clientY + pane.y),
            });
        }
    }, [setNodeMenu]);

    const onEdgeContextMenu = useCallback((event: MouseEvent, edge: Edge) => {
        // Prevent native context menu from showing
        event.preventDefault();

        const pane = ref.current.getBoundingClientRect();
        setEdgeMenu({
            id: edge.id,
            top: event.clientY < pane.height - 200 && (event.clientY - pane.y),
            left: event.clientX < pane.width - 200 && (event.clientX - pane.x),
            right: event.clientX >= pane.width - 200 && (pane.width - event.clientX + pane.x),
            bottom: event.clientY >= pane.height - 200 && (pane.height - event.clientY + pane.y),
        });
    }, [setEdgeMenu]);

    // Close the context menu if it's open whenever the window is clicked.
    const onPaneClick = useCallback(() => {
        setNodeMenu(null);
        setEdgeMenu(null);
    }, [setNodeMenu, setEdgeMenu]);

    const onMouseDownCapture = useCallback((event: MouseEvent) => {
        event.preventDefault();
        const parentElement = (event.target as globalThis.Node).parentElement;

        if (menuRef.current && !menuRef.current.contains(parentElement)) {
            setNodeMenu(null);
            setEdgeMenu(null);
        }

    }, [setNodeMenu, setEdgeMenu]);

    return (
        <div className={styles.workflowVisualizationPolicyConfigsContainer}>
            <ReactFlow
                ref={ref}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodes={nodes}
                edges={edges}
                onPaneClick={onPaneClick}
                onMouseDownCapture={onMouseDownCapture}
                onNodeContextMenu={onNodeContextMenu}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onEdgeContextMenu={onEdgeContextMenu}
                nodesDraggable={true}
                elementsSelectable={true}
                fitView={true}
            >
                <Controls
                    showInteractive={false}
                    position={"top-right"}
                />
                { /** @ts-ignore */}
                <MiniMap pannable={true} nodeColor={"black"} />
                { /** @ts-ignore */}
                <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                {nodeMenu && (
                    <WorkflowVisualizationServiceContextMenu
                        onPaneClick={onPaneClick}
                        {...nodeMenu}
                        menuRef={menuRef}
                        hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                        onClickArchiveService={onClickArchiveService}
                        onClickOpenServiceDetails={onClickOpenServiceDetails}
                        onClickSetAsPrimaryService={onClickSetAsPrimaryService}
                        primaryRpServiceId={primaryRpServiceId}
                        rpServices={rpServices}
                    />
                )}
                {edgeMenu && (
                    <WorkflowVisualizationPolicyDecisionContextMenu
                        onPaneClick={onPaneClick}
                        {...edgeMenu}
                        edge={edges.find(e => e.id === edgeMenu.id)}
                        menuRef={menuRef}
                        onClickOpenPolicyDecisionDetails={onClickOpenPolicyDecisionDetails}
                        rpServices={rpServices}
                    />
                )}
            </ReactFlow>
        </div>
    );
};